import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import CourseList from '../components/Courses/CourseList';
import QuickStats from '../components/Courses/QuickStats';
import Modal from '../components/Layout/Modal';
import CourseForm from '../components/Courses/CourseForm';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import ElevatedButton from '../components/Layout/ElevatedButton';

const Container = styled.div`
  height: 100%;
  display: flex;
  background: ${props => props.theme.background};
  overflow: hidden;

  @media (max-width: 768px) {
    overflow-y: auto;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    flex: 1;
    overflow-y: auto;
  }
`;

const CoursesHeader = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme.background};

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const ToggleButton = styled.button`
  background: ${props => props.active ? props.theme.buttonPrimary : props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  color: ${props => props.theme.text};
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? props.theme.buttonPrimary : props.theme.cardBackgroundHover};
  }
`;

const CoursesTitle = styled.h1`
  color: ${props => props.theme.text};
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0;
  }
`;

const CurrentDate = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0;
  font-size: 0.9375rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const DesktopStats = styled.div`
  padding-top: 0.25rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Courses = () => {
  const { user } = useAuth();
  const { settings, courses } = useData(); 
  const [viewMode, setViewMode] = useState('active');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const isPremium = user?.subscriptionStatus !== 'canceled' && user?.subscriptionStatus !== 'incomplete_expired' && ((user?.subscription && user?.subscription !== 'free' && user?.subscription !== 'basic') || (user?.aiTier && user?.aiTier !== 'free' && user?.aiTier !== 'basic'));
  const isCompact = settings?.display?.compactMode;

  const handleAddNew = () => {
    if (!isPremium && courses && courses.length >= 5) {
      toast.error('Free users can only have up to 5 courses. Please upgrade your plan to add more.');
      return;
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFormSubmit = () => {
    setIsModalOpen(false);
  };

  return (
    <Container>
      <MainContent>
        <CoursesHeader>
          <HeaderTop>
            <CoursesTitle>Courses</CoursesTitle>
            <HeaderActions>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <ToggleButton active={viewMode === 'active'} onClick={() => setViewMode('active')}>
                  Active
                </ToggleButton>
                <ToggleButton active={viewMode === 'completed'} onClick={() => setViewMode('completed')}>
                  Completed
                </ToggleButton>
              </div>
              <ElevatedButton onClick={handleAddNew}>Add Course</ElevatedButton>
            </HeaderActions>
          </HeaderTop>
          <CurrentDate>{new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</CurrentDate>
          <DesktopStats compact={isCompact}>
            <QuickStats />
          </DesktopStats>
        </CoursesHeader>
        <CourseList viewMode={viewMode} />
        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          <CourseForm onSubmit={handleFormSubmit} onCancel={handleCloseModal} />
        </Modal>
      </MainContent>
    </Container>
  );
};

export default Courses;
